import { Controller } from 'stimulus'
import $ from 'jquery'
import './panes_component.scss'
import { handleDataFocus } from '~/common/behaviors/form_focus'
import { ancestorHasDisplayNone } from '~/common/helpers/dom'
import {
  paneFromEvent,
  resolvePop,
  setActivePane,
  stackPanes,
  topPane,
} from './panes_component_helpers.js'

// Fallback (a bit longer than the 300ms CSS slide) so a slide whose transform
// never actually changes still finalizes instead of stranding the pane.
const SLIDE_FALLBACK_MS = 400

export default class extends Controller {
  static targets = ['pane']

  paneTargetConnected() {
    if (this.#panes().length == 1 && !this.isTransitioning()) {
      // This can happen on connect initially but also if the first pane is lazy loaded. In which case it won't have the active class added
      // The side-effect of which is that the first push after that would animate weirdly'
      this.#setActive(this.#lastPane())
    }
  }

  /* ====== Public API ====== */
  // Clones content from a sibling <template> element and pushes it as a new pane.
  // Used when the pane HTML contains <template> elements (e.g. portalled forms, object pickers)
  // that can't survive round-tripping through a data attribute + jQuery HTML parsing.
  pushFromTemplate(event) {
    event.stopPropagation()
    const selector = event.params?.template
    const tpl = selector && document.querySelector(selector)
    if (!tpl?.content) return

    this.push(
      new CustomEvent('push-content', {
        detail: { content: tpl.content.cloneNode(true) },
      })
    )
  }

  // Expects the the rendered pane html to be in event.detail.content OR in event.params.content
  // i.e a result of vc.pane do ... end
  push(event) {
    event.stopPropagation()

    $(this.element).append(event.detail?.content || event.params?.content)
    // Read the pane we just appended straight off the DOM. Going through paneTargets
    // meant the active marker could land on the pane below the new one, which the CSS
    // then painted alongside it instead of behind it.
    const addedPane = this.#lastPane()

    this.dispatch('push-started')
    this.beginTransition(addedPane)

    this.#setActive(addedPane)
    $(addedPane).changed()

    this.#afterSlide(addedPane, () => {
      this.endTransition()
      if (event.detail?.removeMiddlePanes || event.params?.removeMiddlePanes) {
        const middlePanes = this.#panes().slice(1, -1)
        middlePanes.forEach((pane) => pane.remove())
      }
      handleDataFocus(this.#lastPane())
      this.repositionAncestorDropdown()
    })
  }

  pop(event) {
    event.stopPropagation()

    const { pane, previousPane } = resolvePop(this.#panes(), event?.target)

    if (!previousPane) {
      console.log('Cant pop last pane')
      return
    }

    this.dispatch('pop-started')
    this.beginTransition(previousPane)
    this.#setActive(previousPane)

    this.#afterSlide(pane, () => {
      pane.remove()
      this.endTransition()
      this.dispatch('popped', { detail: { pane } })
      this.repositionAncestorDropdown()
    })
  }

  // Pops all panes except the first one. Without any animation
  popToRoot() {
    this.#panes()
      .slice(1)
      .forEach((pane) => pane.remove())
    // The active pane was almost always one we just removed — re-activate the
    // root so the next push starts from a consistent state (otherwise no pane
    // carries t-pane--active and the slide direction / stacking goes wrong).
    this.#setActive(this.#lastPane())
    this.repositionAncestorDropdown()
  }

  // Same as push except will replace the last pane - without any animation
  // Optionally, if you wish to replace arbitrary panes instead of the last one, you can pass a event.detail.replace_selector
  // And we'll replace all those panes with this new one.
  // Example event.detail = { content: 'new pane html', replace_selector: '#optional-can-be-used-to-replace-all-matching-panes-with-the-new-one' }
  replace(event) {
    event.stopPropagation()
    this.dispatch('replace-started')

    if (event.detail.replace_selector) {
      this.element
        .querySelectorAll(event.detail.replace_selector)
        .forEach((pane) => pane.remove())
    } else {
      // If the event bubbled from a specific pane in *this* stack (e.g. ajax_helpers
      // dispatched on the pane), replace that pane. A nested pane or a wrapping
      // parent-stack pane is ignored — otherwise we'd delete the wrong panel.
      paneFromEvent(this.#panes(), event.target)?.remove()
    }
    $(this.element).append(event.detail?.content || event.params?.content)

    const addedPane = this.#lastPane()
    this.#setActive(addedPane)
    $(addedPane).changed()
    this.repositionAncestorDropdown()
  }

  // Will replace all panes with the new content - without any animation.
  // The content may contain several panes (e.g. a restored drill-down stack) —
  // the last one becomes the active pane.
  set(event) {
    event.stopPropagation()
    this.dispatch('set-started')

    this.#panes().forEach((pane) => pane.remove())
    $(this.element).append(event.detail?.content || event.params?.content)
    this.#panes().forEach((pane) => $(pane).changed())
    this.#setActive(this.#lastPane())
    this.repositionAncestorDropdown()
  }

  // On an arrow left keypress, if the press inside an input in this pane and it had no value, let's pop the pane
  // Maybe we should do this for more things (i.e doesn't matter if the press was inside an input or not, as long as it was on the pane, pop it, but maybe we should not?)
  // What we absolutely SHOULDN'T do is pop the pane if the press was on an input that had a value
  onArrowLeft(event) {
    const input = event.target.closest('input')
    if (!input) return
    if (input.value) return
    event.preventDefault()
    event.stopPropagation()
    this.dispatch('pop')
  }
  /* ====== End Public API ====== */

  #panes() {
    return stackPanes(this.element)
  }

  #lastPane() {
    return topPane(this.#panes())
  }

  // Make the given pane the only active one (move the t-pane--active marker to it).
  #setActive(pane) {
    setActivePane(this.#panes(), pane)
  }

  // Run onEnd once the pane's transform slide finishes. Falls back to
  // transitioncancel and a timeout so a slide that never actually animates
  // (start === end, or an interrupted transition) can't strand the pane.
  #afterSlide(pane, onEnd) {
    if (
      ancestorHasDisplayNone(pane) ||
      window.noAnimationsMiddlewareActive ||
      this.element.offsetWidth === 0
    ) {
      return onEnd()
    }

    const ac = new AbortController()
    const finish = () => {
      ac.abort()
      clearTimeout(timer)
      onEnd()
    }
    // Only react to the slide itself — transitionend bubbles, so a child's
    // hover/focus transition (or any non-transform property) must not end it early.
    const onTransform = (e) => {
      if (e.target === pane && e.propertyName === 'transform') finish()
    }
    pane.addEventListener('transitionend', onTransform, { signal: ac.signal })
    pane.addEventListener('transitioncancel', onTransform, {
      signal: ac.signal,
    })
    const timer = setTimeout(finish, SLIDE_FALLBACK_MS)
  }

  // Set variables and classes for the css to hook into
  beginTransition(paneToBeActive) {
    paneToBeActive.style.transitionDuration = '0ms'
    this.element.classList.add('t-panes--transitioning')

    const newWidth = paneToBeActive.offsetWidth + 'px'
    this.element.style.width = newWidth
    this.element.style.setProperty('--pane-width', newWidth)

    // Trigger a reflow to make sure the transition works
    void paneToBeActive.offsetWidth
    paneToBeActive.style.transitionDuration = ''
  }
  endTransition() {
    this.element.classList.remove('t-panes--transitioning')
    this.element.style.width = ''
  }

  isTransitioning() {
    return this.element.classList.contains('t-panes--transitioning')
  }

  // If these panes are rendered inside a tippy popper (e.g. a dropdown), tell tippy to
  // recompute its position now that the active pane's size may have changed.
  repositionAncestorDropdown() {
    const popper = this.element.closest('.tippy-box')?.parentElement
    popper?.tippy?.setProps({})
  }
}
